import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { DEPARTAMENTOS_COLOMBIA as DEPARTAMENTOS } from "@/lib/departamentos";

/**
 * Modal reutilizable para crear un cliente rápido (por ejemplo, desde el flujo de pedido).
 * Los campos son los mismos que el formulario completo de `/clientes`:
 * nombre, nombre comercial, cédula, NIT, teléfono principal, teléfono alterno,
 * departamento (dropdown), pueblo/ciudad, dirección, email y segmento.
 * El backend forzará que el cliente quede asignado al vendedor logueado si el usuario no es director.
 */
const SEGMENTOS = [
  { value: "minorista", label: "Minorista" },
  { value: "mayorista", label: "Mayorista" },
  { value: "vip", label: "VIP" },
];

const EMPTY = {
  nombre: "", nombre_comercial: "",
  tipo_documento: "cc", numero_documento: "",
  telefono: "", telefono_alterno: "", email: "",
  departamento: "", ciudad: "", direccion: "",
  segmento: "minorista", notas: "",
};

export default function QuickClienteModal({ open, onOpenChange, onCreated, defaultNombre = "" }) {
  const [form, setForm] = useState({ ...EMPTY, nombre: defaultNombre });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, nombre: defaultNombre });
  }, [open, defaultNombre]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clientes", {
        nombre: form.nombre.trim(),
        nombre_comercial: form.nombre_comercial.trim() || null,
        tipo_documento: form.tipo_documento || null,
        numero_documento: form.numero_documento.trim() || null,
        telefono: form.telefono.trim() || null,
        telefono_alterno: form.telefono_alterno.trim() || null,
        email: form.email.trim() || null,
        departamento: form.departamento || null,
        ciudad: form.ciudad.trim() || null,
        direccion: form.direccion.trim() || null,
        segmento: form.segmento,
        notas: form.notas.trim() || null,
      });
      toast.success(`Cliente "${data.nombre}" creado y asignado a ti`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el cliente"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quick-cliente-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-primary tracking-wider">Nuevo cliente rápido</div>
              <DialogTitle className="font-display text-2xl mt-1">Registrar cliente</DialogTitle>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            El cliente quedará asignado a ti automáticamente. Solo el director puede reasignarlo después.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <Label>Nombre*</Label>
            <Input required autoFocus value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="h-11 mt-1" placeholder="Nombre del contacto principal" data-testid="qc-nombre" />
          </div>
          <div>
            <Label>Nombre comercial</Label>
            <Input value={form.nombre_comercial}
              onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })}
              className="h-11 mt-1" placeholder="Ej: Boutique Colores S.A.S." data-testid="qc-nombre-comercial" />
            <p className="text-[10px] text-muted-foreground mt-1">Se usa en las remisiones y facturas.</p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Tipo de documento</Label>
              <div className="flex gap-2 mt-1">
                {[["cc", "Cédula (C.C.)"], ["nit", "NIT"]].map(([v, l]) => (
                  <label key={v}
                    className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-lg border cursor-pointer text-sm font-medium transition ${form.tipo_documento === v ? "bg-primary/10 border-primary text-primary" : "bg-card border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`qc-tipo-doc-${v}`}>
                    <input type="radio" name="tipo_documento" className="accent-primary"
                      checked={form.tipo_documento === v}
                      onChange={() => setForm({ ...form, tipo_documento: v })} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Número de {form.tipo_documento === "nit" ? "NIT" : "cédula"}</Label>
              <Input value={form.numero_documento}
                onChange={(e) => setForm({ ...form, numero_documento: e.target.value })}
                className="h-11 mt-1"
                placeholder={form.tipo_documento === "nit" ? "Ej: 900123456-7" : "Sin puntos"}
                data-testid="qc-numero-doc" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Teléfono principal</Label>
              <Input value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="h-11 mt-1" placeholder="Ej: 300 123 4567" data-testid="qc-telefono" />
            </div>
            <div>
              <Label>Teléfono alterno</Label>
              <Input value={form.telefono_alterno}
                onChange={(e) => setForm({ ...form, telefono_alterno: e.target.value })}
                className="h-11 mt-1" placeholder="Opcional" data-testid="qc-telefono-alterno" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Departamento</Label>
              <select value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm" data-testid="qc-departamento">
                <option value="">— Selecciona —</option>
                {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label>Pueblo / Ciudad</Label>
              <Input value={form.ciudad}
                onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                className="h-11 mt-1" placeholder="Ej: Rionegro, La Ceja..." data-testid="qc-ciudad" />
            </div>
          </div>
          <div>
            <Label>Dirección</Label>
            <Input value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              className="h-11 mt-1" placeholder="Dirección exacta donde se entregará el pedido" data-testid="qc-direccion" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-11 mt-1" data-testid="qc-email" />
          </div>
          <div>
            <Label>Segmento</Label>
            <div className="flex gap-2 mt-1">
              {SEGMENTOS.map((s) => (
                <button type="button" key={s.value} onClick={() => setForm({ ...form, segmento: s.value })}
                  className={`flex-1 h-11 px-3 rounded-md border text-sm font-medium transition ${form.segmento === s.value ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:bg-accent"}`}
                  data-testid={`qc-seg-${s.value}`}>{s.label}</button>
              ))}
            </div>
          </div>
          <div className="pt-2 flex justify-end gap-2 sticky bottom-0 bg-card pb-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="qc-save">
              {saving ? "Creando..." : "Crear y usar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
